import React from 'react';
import { AppProps } from 'next/app';
import Head from 'next/head';
import { Provider } from 'next-auth/client';
import * as Sentry from '@sentry/react';
import { RewriteFrames } from '@sentry/integrations';

import { AppFrame } from '@edx/components/app-frame';
import { useTrackCampaigns } from '@edx/hooks/use-track-campaigns';
import { route } from '@edx/helpers/url';

import {
  EDX_EMAIL,
  EDX_FAVICON_COLOR,
  EDX_META_DESCRIPTION,
  EDX_NAME,
  EDX_SHORT_NAME,
  MAIN_MENU,
} from '@edb/config/app';
import { FOOTER_COLUMNS } from '@edb/config/footer';
import '../styles/app.css';

if (process.env.NEXT_PUBLIC_SENTRY_DSN) {
  const integrations = [];
  if (process.env.NEXT_IS_SERVER === 'true' && process.env.NEXT_PUBLIC_SENTRY_SERVER_ROOT_DIR) {
    // For Node.js, rewrite Error.stack to use relative paths, so that source
    // maps starting with ~/_next map to files in Error.stack with path
    // app:///_next
    integrations.push(
      new RewriteFrames({
        iteratee: (frame): Sentry.StackFrame => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          frame.filename = frame.filename.replace(process.env.NEXT_PUBLIC_SENTRY_SERVER_ROOT_DIR, 'app:///');
          frame.filename = frame.filename.replace('.next', '_next');
          return frame;
        },
      })
    );
  }

  Sentry.init({
    enabled: process.env.NODE_ENV === 'production',
    integrations,
    dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
    release: process.env.NEXT_PUBLIC_COMMIT_SHA,
  });
}

type Props = AppProps & {
  err: never;
};

const WEB_PAGE_SCHEMA_ORG = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  publisher: {
    '@type': 'Organization',
    '@id': 'edxOrganization',
    url: route(''),
    name: EDX_NAME,
    description: EDX_META_DESCRIPTION,
    logo: { '@type': 'ImageObject', url: route('img/escueladebajistas_logo.png'), width: 600, height: 600 },
    sameAs: [
      'https://www.facebook.com/escueladebajistas',
      'https://www.instagram.com/escueladebajistas',
      'https://twitter.com/escuelabajistas',
    ],
  },
};

const App: React.FC<Props> = ({ Component, pageProps, err }) => {
  useTrackCampaigns(`${EDX_SHORT_NAME}::campaign`);

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const simpleNavBar = Boolean(Component?.simpleNavBar);
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const simpleFooter = Boolean(Component?.simpleFooter);
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const navBar = Component?.navBar;

  return (
    <Provider session={pageProps.session}>
      <AppFrame
        initialApolloState={pageProps.initialApolloState}
        navBar={navBar}
        navBarLinks={MAIN_MENU}
        simpleNavBar={simpleNavBar}
        projectName={EDX_NAME}
        projectShortName={EDX_SHORT_NAME}
        email={EDX_EMAIL}
        footerColumns={FOOTER_COLUMNS}
        faviconColor={EDX_FAVICON_COLOR}
        simpleFooter={simpleFooter}
      >
        <Head>
          <title key="title">{EDX_NAME}</title>
          <meta key="description" name="description" content={EDX_META_DESCRIPTION} />

          {/* Open Graph */}
          <meta key="og:site_name" property="og:title" content={EDX_NAME} />
          <meta key="og:title" property="og:title" content={EDX_NAME} />
          <meta key="og:description" property="og:description" content={EDX_META_DESCRIPTION} />
          <meta key="og:locale" property="og:locale" content="es_ES" />

          {/* Facebook */}
          <meta property="fb:admins" content="1531438395" />

          {/* Twitter - both are static */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:site" content="EscuelaBajistas" />

          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(WEB_PAGE_SCHEMA_ORG) }}
          />
        </Head>
        <Component {...pageProps} err={err} />
      </AppFrame>
    </Provider>
  );
};

export default App;
